import React, { useState, DragEvent, ChangeEvent, useEffect } from "react";
import styles from "./ImageUploader.module.css";
import cn from "classnames";
import { Image } from "react-bootstrap";
import { ASSESTS } from "../../../constant/assest";

interface IImageUploaderProps {
  className?: string;
  onChange: (file: File | null) => void;
  errorText?: string;
  showError?: boolean;
  value: File | null;
  isUploading: boolean;
  uploadProgress: number;
}

const ImageUploader: React.FC<IImageUploaderProps> = ({ className, onChange, errorText, showError, value, isUploading, uploadProgress }) => {
  const [preview, setPreview] = useState<{ url: string; type: string } | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (value) {
      setPreview({ url: URL.createObjectURL(value), type: value.type });
    } else {
      setPreview(null);
    }
  }, [value]);

  const handleFile = (selectedFile: File) => {
    if (selectedFile && (selectedFile.type.startsWith("image/") || selectedFile.type.startsWith("video/"))) {
      onChange(selectedFile);
    } else {
      onChange(null);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {      
      handleFile(e.target.files[0]);
    }
  };

  // Clean up preview URL when file changes or unmounts
  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  return (
    <>
    <div
      className={cn(styles.uploadCard, dragActive && styles.active, className, preview?.url && styles.hasPreview)}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
>
      <div className={styles.overlay}>
        {!preview ? (
          <div className={styles.content}>
            <div className={styles.header}>
              <div className={styles.icon}>
                <Image src={ASSESTS.CLOUDUPLOAD} alt="file" width={38} height={38} />
              </div>
              <div className={styles.detail}>
                <div className={styles.title}>Drag and drop files here</div>
                <div className={styles.condition}>Max. file size: 20 GB</div>
              </div>
            </div>
            <div className={styles.orText}>Or</div>
            <label className={styles.browseBtn}>
              Browse
              <input
                type="file"
                accept="image/*,video/*"
                onChange={handleChange}
                hidden
              />
            </label>
          </div>
        ) : (
          <div className={styles.previewContainer}>
            {preview.type.startsWith("image/") ? (
              <img src={preview.url} alt="Preview" className={styles.previewMedia} />
            ) : (
              <video
                src={preview.url}
                controls
                className={styles.previewMedia}
              />
            )}
            <button
              className={styles.removeBtn}
              onClick={() => {
                onChange(null);
              }}
            >
              X
            </button>
            {isUploading && (
              <div className={styles.uploadProgress}>
                  <progress value={uploadProgress} max="100" />
                  <div className={styles.progressText}>{uploadProgress}%</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {showError && <div className={styles.errorText}>{errorText}</div> }
  </>
  );
};

export default ImageUploader;
